import { useState } from "react";
import { useEmailTemplates } from "@hooks/useEmailTemplates";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import SuccessModal from "@components/common/SuccessModal";
import FailureModal from "@components/common/FailureModal";
import PageTitleWithBack from "@components/common/PageTitleWithBack";
import { validateEmailForm } from "@utils/validators";
import { sanitizeFormValues } from "@utils/sanitize";

const EmailTemplateModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formName = location.state?.formName || "Untitled Form";
  const template_id = location.state?.template_id || null;
  const patient_id = location.state?.patient_id || null;
  const patient_name = location.state?.patient_name || "Unknown";
  const [form, setForm] = useState({
    date: "",
    time: "",
    // clinic: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const { sendEmailTemplates, isLoading } = useEmailTemplates();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (prev[name]) {
        return { ...prev, [name]: "" };
      }
      return prev;
    });
  };

  const validate = () => validateEmailForm(form);

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const payload = sanitizeFormValues({
      ...form,
      patient_id,
      template_id,
      patient_name,
      formName,
    });
    try {
      const { success, error } = await sendEmailTemplates(payload);
      if (success) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate(-1);
        }, 3000);
      } else {
        setErrorMessage(error.message || "Something went wrong.");
        setShowFailure(true);
        setTimeout(() => setShowFailure(false), 4000);
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Sticky Navbar */}
      <div className="sticky top-0 z-50">
        <Navbar showFieldSearch={false} />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-5">
        <PageTitleWithBack title={`${patient_name} - ${formName}`} onBack={() => navigate(-1)} />
        {/* <div className="mb-6">
          <label
            className={`block text-sm font-medium mb-2 ${
              errors.date ? "text-red-700" : "text-gray-900"
            }`}
          >
            Clinic name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="clinic"
            className={`w-full px-3 py-2 bg-gray-50 rounded-lg shadow-sm focus:outline-none ${
              errors.clinic
                ? "bg-red-50 border border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500"
                : "border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            } `}
            value={form.clinic}
            onChange={handleInputChange}
            placeholder="Clinic name"
          />
          {errors.clinic && (
            <p className="mt-2 text-sm text-red-600">{errors.clinic}</p>
          )}
        </div> */}
        <div className="mb-6">
          <label
            className={`block text-sm font-medium mb-2 ${
              errors.date ? "text-red-700" : "text-gray-900"
            }`}
          >
            Appointment's date <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 items-center">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01v.01H8V13Zm4 0h.01v.01H12V13Zm4 0h.01v.01H16V13Zm-8 4h.01v.01H8V17Zm4 0h.01v.01H12V17Zm4 0h.01v.01H16V17Z"
                  />
                </svg>
              </span>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleInputChange}
                placeholder="date"
                className={`w-full pl-10 pr-3 py-2 rounded-md bg-gray-50 text-sm text-gray-900  ${
                  errors.date
                    ? "bg-red-50 border border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500"
                    : "border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
            </div>
          </div>{" "}
          {errors.date && (
            <p className="mt-2 text-sm text-red-600">{errors.date}</p>
          )}
        </div>
        <div className="mb-6">
          <label
            className={`block text-sm font-medium mb-2 ${
              errors.time ? "text-red-700" : "text-gray-900"
            }`}
          >
            Appointment's time <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4 items-center">
            {/* Time Input with Icon */}
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </span>
              <input
                className={`w-full pl-10 pr-3 py-2 rounded-md bg-gray-50 text-sm text-gray-900  ${
                  errors.time
                    ? "bg-red-50 border border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500"
                    : "border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
                type="time"
                name="time"
                value={form.time}
                onChange={handleInputChange}
              />
            </div>
          </div>
          {errors.time && (
            <p className="mt-2 text-sm text-red-600">{errors.time}</p>
          )}
        </div>
      </div>

      {/* Fixed Save Button */}
      <div className="sticky bottom-0 w-full bg-white px-4 py-3 z-50">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-blue-600 flex justify-center items-center gap-2 text-white w-full py-3 rounded-lg text-base font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
        >
          <svg
            className="w-5 h-5 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              d="M12 2a1 1 0 0 1 .932.638l7 18a1 1 0 0 1-1.326 1.281L13 19.517V13a1 1 0 1 0-2 0v6.517l-5.606 2.402a1 1 0 0 1-1.326-1.281l7-18A1 1 0 0 1 12 2Z"
              clipRule="evenodd"
            />
          </svg>
          {isLoading ? "Sending..." : "Send Form To Patient"}
        </button>
      </div>
      <SuccessModal
        isOpen={showSuccess}
        title={"Form sent successfully!"}
        message={"Waiting for patient to complete."}
      />
      <FailureModal
        isOpen={showFailure}
        titleError={"Failed to send Form"}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default EmailTemplateModal;
